<template>
  <div>
    <p class="text--small">{{ $t('project.signing-checklist.add.SigningChecklistSectionDocumentSharingOptions.sectionIntroduction')}}</p>
    <a class="app-link learnMoreLink"
      :href="learnMoreLinkUrl"
      target="_blank"
    >
      {{ $t("common.learnMore") }}
      <font-awesome-icon :icon="['far', 'external-link']" class="ml-1" size="xs"/>
    </a>

    <v-radio-group
      :model-value="shareType"
      class="shareType__container mt-6 mb-0"
      :disabled="isEditingLimited"
      hide-details
      required
    >
      <div
        v-for="option in shareTypeOptions"
        class="shareType__card"
        :key="`option-${option}`"
        @click="onShareTypeChange(option)"
      >
        <v-radio :true-value="option">
          <template #label>
            <h4>{{
                $t(`project.signing-checklist.add.SigningChecklistSectionDocumentSharingOptions.shareTypeOptions.${option}`)
              }}</h4>
          </template>
        </v-radio>
      </div>
    </v-radio-group>

    <AppSubscriptionMembersPicker v-if="shareType === SHARE_TYPE_OPTIONS.CUSTOM"
                                  :selected-members="fileWithUsers || []"
                                  @update-members-selection="onWithUsersChange"
    />

    <v-checkbox v-if="canRequireNotificationSend"
                v-model="shareSendNotif"
                :label="$t('project.signing-checklist.add.SigningChecklistSectionDocumentSharingOptions.sendNotifLabel')"
                hide-details
                @update:modelValue="onSendNotifChange"
    ></v-checkbox>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'

import AppSubscriptionMembersPicker from '@/common/subscription-members-picker/AppSubscriptionMembersPicker.vue'
import { SHARE_TYPE_OPTIONS, type ShareType } from '@/common/utils/shareTypes'

export default defineComponent({
  name: 'SigningChecklistSectionDocumentSharingOptions',
  components: {
    AppSubscriptionMembersPicker,
  },
  props: {
    shareType: {
      type: String as PropType<ShareType>,
    },
    fileRights: {
      type: Object,
      required: true,
    },
    fileWithUsers: {
      type: Array as PropType<Array<number>>,
      default: () => [],
    },
    isEditingLimited: {
      type: Boolean,
      required: true,
    },
  },
  data () {
    return {
      SHARE_TYPE_OPTIONS,
      shareSendNotif: false,
    }
  },
  computed: {
    canRequireNotificationSend (): boolean {
      return this.shareType === SHARE_TYPE_OPTIONS.PUBLIC || this.shareType === SHARE_TYPE_OPTIONS.CUSTOM
    },
    shareTypeOptions (): Array<ShareType> {
      return Object.values(SHARE_TYPE_OPTIONS)
    },
    learnMoreLinkUrl (): string {
      if (this.$i18n.locale === 'fr') {
        return 'https://help.closd.com/fr/'
      }

      return 'https://help.closd.com/en/'
    },
  },
  methods: {
    onWithUsersChange (withUsers: Array<number>) {
      this.$emit('with-users-change', withUsers)
    },
    onSendNotifChange (sendNotif: boolean | null) {
      this.$emit('send-notif-change', !!sendNotif)
    },
    onShareTypeChange (newShareType: ShareType | null) {
      this.$emit('share-type-change', newShareType)
    },
  },
})
</script>

<style scoped lang="scss">
.shareType__container {
  :deep( .v-selection-control-group) {
    width: 100%;
    display: grid;
    gap: 24px;
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  }
}

.shareType__card {
  justify-self: stretch;
  border: 1px solid #DEE2E6;
  border-radius: 4px;
  overflow: hidden;
  margin: 0 !important;
  cursor: pointer;
}
.v-selection-control {
  padding-block: 16px;
  padding-left: 24px;
  padding-right: 8px;

  &--dirty {
    outline: 2px solid rgb(var(--v-theme-primary));
    outline-offset: -2px;
    background-color: rgb(var(--v-theme-primary-lighten-4));
  }
}
</style>
